/* eslint-disable @typescript-eslint/no-explicit-any */
import Modal from 'components/calls/templates/Modal';
import React from 'react';
import ReactModal from 'react-modal';
import { Form, Label, Textarea, Button, FormControl, Input, ErrorContainer } from './style';
import { getUser, uploadToZapier } from 'api';
import { toast } from 'react-toastify';
import { ErrorMessage, SuccessMessage } from 'components/calls/atoms/Toast';
import { CircleLoader } from 'components/class/psa/psa-row/psa-upload-button/styles';
import { getFormattedDate } from 'components/calls/utils';
import { v4 as uuidv4 } from 'uuid';

const CLOUD_NAME = 'mindbodygreen';
const UPLOAD_PRESET = 'psa-report-images';

const ReportIssueModal = (
  props: ReactModal.Props & {
    close: () => void;
    roomId?: string;
    videoId?: string;
    assignmentId?: string;
    userId?: string;
    psa?: any;
  }
) => {
  const [formData, setFormData] = React.useState<{
    issue: string;
  }>({
    issue: '',
  });
  const [formError, setFormError] = React.useState<string>('');
  const [uploading, setUploading] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const screenshotRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) {
      console.error('Please select a file');
      return;
    }
    setFile(selectedFile);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormError('');
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const cleanup = () => {
    setFormData({
      issue: '',
    });
    setFile(null);
    setUploading(false);
    if (screenshotRef.current) {
      screenshotRef.current.value = '';
    }
  };

  const beforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = 'Your upload is still in progress. Do you want to leave this page?';
  };

  const handleUploadImageToCloudinary = async () => {
    const formData = new FormData();
    const uniqueUploadId = uuidv4();
    formData.append('file', file);
    formData.append('cloud_name', CLOUD_NAME);
    formData.append('upload_preset', UPLOAD_PRESET);
    formData.append('public_id', uniqueUploadId);

    try {
      const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Unique-Upload-Id': uniqueUploadId,
        },
      });

      if (!response.ok) {
        throw new Error('Image upload failed.');
      }

      return await response.json();
    } catch (error) {
      toast.error(<ErrorMessage message="Something went wrong, please try again" />, {
        autoClose: 5000,
        position: 'top-right',
      });
      console.error('Error uploading image:', error);
      setUploading(false);
    } finally {
      window.removeEventListener('beforeunload', beforeUnload);
    }
  };

  const handleUploadToZapier = async (screenshotUrl: string) => {
    const PUBLIC_SENDBIRD_APP_ID = process.env.NEXT_PUBLIC_SENDBIRD_APP_APP_ID;

    try {
      let userData = null;
      if (props?.userId && !String(props?.userId).includes('-')) {
        userData = await getUser(Number(props?.userId));
      }

      const screenshotLink = screenshotUrl !== '' ? screenshotUrl : undefined;
      const sendbirdRoomLink = props?.roomId
        ? `https://dashboard.sendbird.com/${PUBLIC_SENDBIRD_APP_ID}/calls/group-calls/${props?.roomId}`
        : undefined;

      const videoWatchLink = props.videoId
        ? // eslint-disable-next-line max-len
          `https://www.mindbodygreen.com/user/classes/calls/watch/${props.assignmentId}/${props.videoId}?userId=${props?.userId}`
        : undefined;
      const email = userData?.emailAddress ? userData?.emailAddress : 'Guest';

      const zapierData = {
        user_id: String(props?.userId),
        user_email: email,
        message: formData.issue,
        date: getFormattedDate(new Date().toISOString()),
        screenshot_link: screenshotLink,
        issue_type: props.videoId ? 'Video' : 'Call',
        room: sendbirdRoomLink,
        video: videoWatchLink,
      };

      await uploadToZapier(zapierData);

      props.close();
      toast(
        <SuccessMessage message="Your report has been successfully sent. Thank you for bringing this issue to our attention." />,
        { autoClose: 5000, position: 'top-right' }
      );
    } catch (err) {
      console.error(err);
      toast.error(<ErrorMessage message="Oops! Something went wrong. Please try again." />, {
        autoClose: 5000,
        position: 'top-right',
      });
    } finally {
      window.removeEventListener('beforeunload', beforeUnload);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    window.addEventListener('beforeunload', beforeUnload);

    if (!formData.issue) {
      setFormError('Please describe the issue in detail before submitting.');
      window.removeEventListener('beforeunload', beforeUnload);
      return;
    }
    setUploading(true);

    let cloudinaryScreenshotURL = '';
    if (file) {
      const cloudinaryFile = await handleUploadImageToCloudinary();
      if (!cloudinaryFile) return;
      cloudinaryScreenshotURL = cloudinaryFile.secure_url || '';
    }

    await handleUploadToZapier(cloudinaryScreenshotURL);

    cleanup();
    setUploading(false);
  };

  return (
    <Modal
      isOpen={props.isOpen}
      close={() => {
        if (uploading) {
          alert('Please wait for the process to finish before closing the modal.');
          return;
        }
        props.close();
      }}
      title="Report An Issue"
      minWidth={320}
      content={
        <Form onSubmit={handleSubmit}>
          <FormControl>
            <Label>
              Issue Details <span>*</span>
            </Label>
            <Textarea
              name="issue"
              placeholder="Please provide a detailed description of the issue"
              value={formData.issue}
              onChange={handleInputChange}
              error={!!formError}
            />
            {formError && (
              <ErrorContainer>
                <p>{formError}</p>
              </ErrorContainer>
            )}
          </FormControl>
          <FormControl>
            <Label>Attach a Screenshot (optional)</Label>
            <Input
              type="file"
              name="screenshot"
              onChange={handleFileChange}
              accept="image/*"
              ref={screenshotRef}
            />
          </FormControl>
          <div>
            <Button type="submit" disabled={uploading}>
              {!uploading ? 'Report Issue' : <CircleLoader />}
            </Button>
          </div>
        </Form>
      }
      footer={{}}
    />
  );
};

export default ReportIssueModal;
